import React, { useEffect, useCallback, useContext, useRef, useState } from 'react';
import { MapContext } from '../maps/MapContext';
import PlaceInfoBox from './PlaceInfoBox';
import useGoogleMap from './useGoogleMap';
import { usePlaceContext } from '../itinerary/PlaceContext';
import MapSearchBar from './MapSearchBar';
import useSuggestionMarkers from '../explore_page/useSuggestionMarkers';

const runtimeConfig = window.__CONFIG__ || {};
const environment =
  runtimeConfig.REACT_APP_ENVIRONMENT ||
  process.env.REACT_APP_ENVIRONMENT ||
  'development';

const GoogleMap = ({
  location,
  suggestions = [],
  initialZoom = 13,
  pageName = 'default',
  readOnly,
  enablePlaceClicks = true
}) => {
  const { googleMapsAPI, mapLoaded } = useContext(MapContext);
  const mapRef = useRef(null);
  const [googleMapInstance, setGoogleMapInstance] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const { googleMap, fetchPlaceDetailsWrapper, fetchPlaceId, matchToOrigin } = useGoogleMap(
    googleMapInstance,
    placesService,
    location,
    initialZoom,
    pageName,
    enablePlaceClicks
  );
  const { state, showPlaceInfoBox, hidePlaceInfoBox, setGoogleMap: setGoogleMapInContext } = usePlaceContext();
  const { isPlaceInfoBoxVisible } = state;

  useEffect(() => {
    if (mapLoaded && googleMapsAPI && googleMapsAPI.ControlPosition && mapRef.current && !googleMapInstance) {
      const mapId =
        environment === 'production'
          ? runtimeConfig.REACT_APP_GOOGLE_MAPS_MAP_ID_PROD || process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_PROD || ''
          : runtimeConfig.REACT_APP_GOOGLE_MAPS_MAP_ID_DEV || process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_DEV || '';
      const mapOptions = {
        center: { lat: location.lat, lng: location.lng },
        zoom: initialZoom,
        mapId,
        mapTypeControl: false,
        mapTypeControlOptions: { position: googleMapsAPI.ControlPosition.TOP_LEFT },
        fullscreenControl: false,
        fullscreenControlOptions: { position: googleMapsAPI.ControlPosition.TOP_RIGHT },
        streetViewControl: false,
        streetViewControlOptions: { position: googleMapsAPI.ControlPosition.DEFAULT },
        zoomControl: true,
        zoomControlOptions: { position: googleMapsAPI.ControlPosition.DEFAULT },
        scrollwheel: true
      };
      const map = new googleMapsAPI.Map(mapRef.current, mapOptions);
      setGoogleMapInstance(map);
      const service = new googleMapsAPI.places.PlacesService(map);
      setPlacesService(service);
    }
  }, [mapLoaded, googleMapsAPI, googleMapsAPI?.ControlPosition, mapRef, googleMapInstance, location, initialZoom, environment]);

  useEffect(() => {
    if (googleMap) {
      setGoogleMapInContext(googleMap);
    }
  }, [googleMap, setGoogleMapInContext]);

  const extendBounds = bounds => {
    const bufferLat = 1;
    const bufferLng = 1;
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const newNorthEast = new googleMapsAPI.LatLng(northEast.lat() + bufferLat, northEast.lng() + bufferLng);
    const newSouthWest = new googleMapsAPI.LatLng(southWest.lat() - bufferLat, southWest.lng() - bufferLng);
    bounds.extend(newNorthEast);
    bounds.extend(newSouthWest);
    return bounds;
  };

  const fitMapToBounds = useCallback(() => {
    if (googleMap && suggestions.length > 0) {
      const bounds = new googleMapsAPI.LatLngBounds();
      suggestions.forEach(place => {
        if (place.suggestion_latitude && place.suggestion_longitude) {
          bounds.extend(new googleMapsAPI.LatLng(place.suggestion_latitude, place.suggestion_longitude));
        }
      });
      const adjustedBounds = extendBounds(bounds);
      googleMap.fitBounds(adjustedBounds);
    }
  }, [googleMap, suggestions, googleMapsAPI]);

  const zoomHandler = pageName === 'ItineraryPage' ? () => {} : fitMapToBounds;

  useSuggestionMarkers(
    googleMapsAPI,
    googleMap,
    suggestions,
    zoomHandler,
    placesService,
    fetchPlaceId,
    fetchPlaceDetailsWrapper,
    showPlaceInfoBox
  );

  return (
    <div className="google-map-container" id="google-map-container">
      {!readOnly && pageName !== 'ExplorePage' && (
        <MapSearchBar
          fetchPlaceDetailsWrapper={fetchPlaceDetailsWrapper}
          googleMapsAPI={googleMapsAPI}
          googleMap={googleMap}
          placesService={placesService}
        />
      )}
      <div ref={mapRef} className="map-container" />
      {isPlaceInfoBoxVisible && (
        <PlaceInfoBox onClose={hidePlaceInfoBox} pageName={pageName} readOnly={readOnly} />
      )}
    </div>
  );
};

export default GoogleMap;
